import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  OutlineBestInDentalIcon,
  OutlineCreditCardIcon,
  OutlineDollarCircleIcon,
  OutlineDocumentTextIcon,
  OutlineUserIcon,
  OutlineUsersIcon,
} from '@tempmee/react-icons';
import { selectOffice } from '~/actions';
import { useIsBestInDentalVisible, useIsOfficeBestInDentalVisible } from '~/domain/BestInDental';
import { useOfficeWorkerClassification } from '~/domain/LocationSettings';
import { useAppSelector } from '~/hooks/useAppSelector';
import { usePaymentExpiration } from '~/hooks/usePaymentExpiration';
import type { IUser } from '~/models/User';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { hasChildOffices } from '~/utils/OfficeUtils';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import type { SettingsMenuRow } from '@tempmee/react-blocks';
import { useScopedOfficeId } from './useScopedOfficeId';

/**
 * The Manage card of one location: the existing pages, unchanged. Those pages
 * act on the office selected in the switcher, so from another location's page
 * a row first switches to that location (the `selectOffice` redirect
 * `ConfirmCandidate` uses) and lands on the page there. Best in Dental and
 * Worker Type follow the viewed location, whichever office is selected: the
 * selected office keeps today's reads (the BID page's history query, the redux
 * state rule), and any other location is asked with its own `x-office`.
 *
 * Visibility follows today's Account menu (`profile/Profile.js` `menuVisibility`)
 * for a page that always shows one office: Billing Details and Payment History
 * always; Invoices for a pre-approved office; Worker Type when the state allows
 * a choice; Worker Type Management on the parent's page when a location sits in
 * a state that allows one; Best in Dental when the program is visible.
 */
export const useManageRows = (office: IUser | undefined) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const childOffices = useAppSelector((state) => state.user.childOffices) ?? [];
  const masterOfficeId = useAppSelector((state) => state.user.user?.id);
  const isSelectedOfficeDualModel = useIsWorkerClassificationEnabled();
  const { visible: isSelectedOfficeBidVisible } = useIsBestInDentalVisible();
  const { isPaymentExpired, isPaymentExpiring } = usePaymentExpiration();
  const scopedOfficeId = useScopedOfficeId();
  const isScopedOffice = !!office && office.id === scopedOfficeId;
  const { data: viewedClassification } = useOfficeWorkerClassification(office?.id, {
    enabled: !!office && !isScopedOffice,
  });
  // Dual model, as `useIsWorkerClassificationEnabled` reads it: both are allowed.
  const isWorkerClassificationEnabled = isScopedOffice
    ? !!isSelectedOfficeDualModel
    : !!(viewedClassification?.contractor && viewedClassification?.employee);
  const isViewedOfficeBidVisible = useIsOfficeBestInDentalVisible(
    office?.id,
    !!office && !isScopedOffice,
  );
  const isBestInDentalVisible = isScopedOffice
    ? isSelectedOfficeBidVisible
    : isViewedOfficeBidVisible;

  if (!office) return [];

  const open = (route: string) => {
    if (isScopedOffice) {
      navigate(route);
    } else {
      dispatch(selectOffice({ officeId: office.id, redirectUrl: route }));
    }
  };

  const allowedWcStates = RemoteConfigUtils.getAllowedWcStates();
  const hasChildInWorkerClassificationState = childOffices.some((child: IUser) =>
    allowedWcStates?.includes(child.address?.[0]?.state),
  );

  // Payment expiry is only known for the office the app is scoped to.
  let paymentAlert: SettingsMenuRow['alert'];
  if (isScopedOffice && isPaymentExpired) paymentAlert = 'error';
  else if (isScopedOffice && isPaymentExpiring) paymentAlert = 'warning';

  const rows: Array<SettingsMenuRow | false> = [
    {
      id: 'billing-details',
      label: 'Billing Details',
      icon: <OutlineCreditCardIcon />,
      alert: paymentAlert,
      onSelect: () => open(routes.paymentInformation),
      testID: 'payment-information',
    },
    {
      id: 'payment-history',
      label: 'Payment History',
      icon: <OutlineDollarCircleIcon />,
      onSelect: () => open(routes.paymentHistory),
      testID: 'payment-history',
    },
    // Any member location, whichever office is selected (UAT, GOTU-15553 comment 91237).
    isBestInDentalVisible && {
      id: 'best-in-dental',
      label: 'Best in Dental',
      icon: <OutlineBestInDentalIcon />,
      onSelect: () => open(routes.bestInDental),
      testID: 'best-in-dental',
    },
    // Any dual-model location, whichever office is selected (UAT, GOTU-15553 comment 91237).
    isWorkerClassificationEnabled && {
      id: 'worker-type',
      label: 'Worker Type',
      icon: <OutlineUserIcon />,
      alert: office.workerClassification?.classificationType ? undefined : 'error',
      onSelect: () => open(routes.workerClassificationType),
      testID: 'worker-type',
    },
    office.id === masterOfficeId &&
      hasChildOffices(childOffices) &&
      hasChildInWorkerClassificationState && {
        id: 'worker-type-management',
        label: 'Worker Type Management',
        icon: <OutlineUsersIcon />,
        onSelect: () => open(routes.workerClassificationManagement),
        testID: 'worker-type-management',
      },
    !!office.is_pre_approved && {
      id: 'invoices',
      label: 'Invoices',
      icon: <OutlineDocumentTextIcon />,
      onSelect: () => open(routes.invoices),
      testID: 'invoices',
    },
  ];

  return rows.filter((row): row is SettingsMenuRow => !!row);
};
